import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Textarea } from "@threadws/jute";
import type { HttpError } from "../../api/generated";
import { zCreatePersona } from "../../api/generated/zod.gen";

export interface PersonaFormValues {
  name: string;
  description: string;
  rules: string;
}

interface PersonaFormProps {
  /** The saved values. When they change, untouched fields update and edited ones are kept. */
  values: PersonaFormValues;
  /** Saves the persona. Throw an `HttpError` to show it on the form. */
  onSubmit: (values: PersonaFormValues) => Promise<unknown>;
  onCancel: () => void;
  submitLabel: string;
}

// The schema is generated from the API spec, so the rules match the server's.
const resolver = zodResolver(zCreatePersona, {
  error: (issue) => {
    if (issue.code === "too_small") return "This is required";
    if (issue.code === "too_big") return `Keep this under ${issue.maximum} characters`;
    return undefined;
  },
});

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-sm text-danger">{message}</p> : null;

/** The fields shared by the create and edit pages. */
export function PersonaForm({ values, onSubmit, onCancel, submitLabel }: PersonaFormProps) {
  const { register, handleSubmit, setError, formState } = useForm({
    resolver,
    values,
    resetOptions: { keepDirtyValues: true },
  });
  const { errors, isSubmitting } = formState;

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (error) {
      const { statusCode, message } = error as HttpError;
      if (statusCode === 409) setError("name", { message: "Another persona has that name" });
      else setError("root.server", { message });
    }
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {/* Trimmed like the server does, so a blank name fails here, not after a request. */}
      <Input
        label="Name"
        error={errors.name?.message}
        {...register("name", { setValueAs: (value: string) => value.trim() })}
      />
      <div className="flex flex-col gap-1">
        <Textarea
          label="Description"
          helpText="Who this persona is. Markdown."
          optional
          rows={6}
          {...register("description")}
        />
        <FieldError message={errors.description?.message} />
      </div>
      <div className="flex flex-col gap-1">
        <Textarea
          label="Rules"
          helpText="What agents must follow for this persona. Markdown."
          optional
          rows={10}
          {...register("rules")}
        />
        <FieldError message={errors.rules?.message} />
      </div>
      <FieldError message={errors.root?.server?.message} />
      <div className="flex justify-end gap-3">
        {/* The pending save still navigates when it finishes, so don't let the user leave first. */}
        <Button variant="ghost" icon="" disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
